// Lightweight WAV header parser used to validate imported files before we trust them.
// We deliberately do not decode full audio here (decodeAudioData can itself alter/resample
// data); this only reads the RIFF/fmt header to verify channel count and container sanity.

export interface WavInspection {
  isValidWav: boolean;
  channelCount: number | null;
  sampleRate: number | null;
  bitsPerSample: number | null;
  durationSeconds: number | null;
  error: string | null;
}

export async function inspectWavFile(file: Blob): Promise<WavInspection> {
  try {
    // "fmt " chunk is always within the first ~64 bytes for canonical WAV files;
    // read a generous prefix to also tolerate extra chunks (e.g. LIST) before "data".
    const headerBytes = await file.slice(0, 4096).arrayBuffer();
    const view = new DataView(headerBytes);

    if (headerBytes.byteLength < 44) {
      return invalidResult('File is too small to be a valid WAV file.');
    }

    const riff = readAscii(view, 0, 4);
    const wave = readAscii(view, 8, 4);
    if (riff !== 'RIFF' || wave !== 'WAVE') {
      return invalidResult('File is not a recognized WAV container.');
    }

    let offset = 12;
    let channelCount: number | null = null;
    let sampleRate: number | null = null;
    let bitsPerSample: number | null = null;
    let dataChunkSize: number | null = null;
    let byteRate: number | null = null;

    while (offset + 8 <= view.byteLength) {
      const chunkId = readAscii(view, offset, 4);
      const chunkSize = view.getUint32(offset + 4, true);

      if (chunkId === 'fmt ') {
        channelCount = view.getUint16(offset + 10, true);
        sampleRate = view.getUint32(offset + 12, true);
        byteRate = view.getUint32(offset + 16, true);
        bitsPerSample = view.getUint16(offset + 22, true);
      } else if (chunkId === 'data') {
        dataChunkSize = chunkSize;
        break;
      }

      offset += 8 + chunkSize + (chunkSize % 2);
    }

    if (channelCount === null || sampleRate === null) {
      return invalidResult('WAV file is missing a valid format chunk.');
    }
    if (channelCount < 1 || channelCount > 8) {
      return invalidResult(`Unsupported channel count (${channelCount}).`);
    }

    let durationSeconds: number | null = null;
    if (dataChunkSize !== null && byteRate) {
      durationSeconds = dataChunkSize / byteRate;
    } else if (byteRate) {
      // "data" chunk size unavailable from header prefix; fall back to file size.
      durationSeconds = file.size / byteRate;
    }

    return {
      isValidWav: true,
      channelCount,
      sampleRate,
      bitsPerSample,
      durationSeconds,
      error: null,
    };
  } catch (error) {
    return invalidResult(error instanceof Error ? error.message : 'Unknown error reading file.');
  }
}

function invalidResult(error: string): WavInspection {
  return {
    isValidWav: false,
    channelCount: null,
    sampleRate: null,
    bitsPerSample: null,
    durationSeconds: null,
    error,
  };
}

function readAscii(view: DataView, offset: number, length: number): string {
  let result = '';
  for (let i = 0; i < length; i++) {
    result += String.fromCharCode(view.getUint8(offset + i));
  }
  return result;
}
